/**
 * Cart release — what the client posts to `POST /api/cart/release` when the
 * buyer dismisses a checkout sheet (quick-checkout from the feed, chat
 * checkout dialog). The server deletes the buyer's `item_reservations` rows
 * and broadcasts an `inventory` event so other tabs/clients refresh
 * availability immediately.
 *
 * The `listingIds` field is OPTIONAL on the wire — the route deletes ALL
 * reservations for the authenticated user regardless of the array's contents.
 * The list is forwarded into the broadcast payload only, so other clients
 * can scope their refresh. We keep it on the contract because both call
 * sites we extract today (`GlobalQuickCheckout.closeFeedSheet`,
 * `ChatCheckoutDialog.close`) populate it, and dropping it would silently
 * disable per-listing refresh hints.
 *
 * @see app/api/cart/release/route.ts — handler
 * @see lib/cart-quick-checkout.ts releaseCart — caller
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface ReleaseCartRequest {
  /**
   * Optional listing ids the buyer was reserving. Forwarded into the
   * `inventory` broadcast payload so other clients can scope a refresh.
   * The server does NOT use this to filter the delete — it removes ALL
   * `item_reservations` rows for the authenticated user.
   */
  listingIds?: string[];
}

// Runtime parse — Zod schema (boundary). `pinned<ReleaseCartRequest>()` is
// the compile-time schema↔interface pin; see `./pin.ts`.
export const ReleaseCartRequestSchema = pinned<ReleaseCartRequest>()(
  z.object({
    listingIds: z.array(z.string().min(1)).optional(),
  }),
);
